import { Injectable } from '@angular/core';
import { ImgDbService } from './img-db.service';
import {
    BehaviorSubject,
    forkJoin,
    map,
    Observable,
    of,
    Subject,
    switchMap,
} from 'rxjs';
import { PagingResult } from '../../shared/models/pageable';
import { ImgWithSelection } from '../model/img-with-selection';
import { UserCacheService } from '../../xtra/user-cache/user-cache-service';
import { AnnotationStateService } from './annotation-state.service';
import { ImageSortingOptions } from '../img-db.component';

@Injectable()
export class ImagesStorageService {
    isEmptyCategorySelected: boolean = false;
    selectedCategoryNames: string[] = [];
    currentPage: number = 1;
    pageSize: number;
    dbId: string;

    selectedImage$: BehaviorSubject<ImgWithSelection> =
        new BehaviorSubject<ImgWithSelection>(null);

    imagesPage$: BehaviorSubject<PagingResult<ImgWithSelection>> =
        new BehaviorSubject<PagingResult<ImgWithSelection>>(null);

    // Storage is ready to fetch images after ImgDbComponent provided ImgDbID and pageSize
    isStorageReady: boolean = false;
    pageChanged$: Subject<void> = new Subject<void>();

    /**
     * Page number whose data is currently in {@link imagesPage$}. Compared
     * against {@link currentPage} so a navigation that crosses a page
     * boundary does not select an image from the previous page while the
     * new page is still being fetched. 0 means nothing has been loaded yet.
     */
    private loadedPage: number = 0;
    /**
     * Index requested by the latest call to {@link updateImageSelection}.
     * Held here while {@link imagesPage$} still reflects an older page and
     * applied as soon as the right page arrives, so concurrent navigations
     * collapse to the most recent intent instead of racing on stale data.
     */
    private pendingSelectionIndex: number | null = null;

    constructor(
        private dbService: ImgDbService,
        private cacheService: UserCacheService,
        private annotationStateService: AnnotationStateService
    ) {}

    setData(pageSize: number, dbId: string) {
        this.dbId = dbId;
        this.pageSize = pageSize;
        this.isStorageReady = true;
    }

    getImages(
        handleBookmarks: boolean = true,
        sortingOption: ImageSortingOptions = 'new-old'
    ): Observable<PagingResult<ImgWithSelection>> {
        if (!this.isStorageReady) {
            return;
        }

        let observables: Observable<any>[] = [
            this.dbService
                .getImages(
                    this.selectedCategoryNames,
                    this.isEmptyCategorySelected,
                    this.dbId,
                    this.currentPage,
                    this.pageSize,
                    sortingOption
                )
                .pipe(
                    switchMap((page: PagingResult<ImgWithSelection>) => {
                        if (
                            page.totalPages > 0 &&
                            page.totalPages + 1 === this.currentPage
                        ) {
                            this.currentPage -= 1;
                            return this.dbService.getImages(
                                this.selectedCategoryNames,
                                this.isEmptyCategorySelected,
                                this.dbId,
                                this.currentPage,
                                this.pageSize,
                                sortingOption
                            );
                        }
                        return of(page);
                    })
                ),
        ];

        if (handleBookmarks) {
            observables.push(
                this.cacheService.loadData(`workspace_image_db_${this.dbId}`)
            );
        }
        return this.updateImages(observables, handleBookmarks);
    }

    private updateImages(
        obs: Observable<any>[],
        handleBookmarks: boolean
    ): Observable<PagingResult<ImgWithSelection>> {
        return forkJoin(obs).pipe(
            map(([page, data]) => {
                if (handleBookmarks) {
                    page.values.forEach((img: ImgWithSelection) => {
                        img.isBookmarked =
                            data?.bookmarkedImageIds?.includes(img.id) ?? false;
                    });
                }
                // loadedPage must be set BEFORE imagesPage$.next(...) so that
                // synchronous subscribers (and tryApplyPendingSelection below)
                // observe a consistent (loadedPage, imagesPage$) pair.
                this.loadedPage = this.currentPage;
                this.imagesPage$.next(page);
                this.tryApplyPendingSelection();
                return page;
            })
        );
    }

    updateImageSelection(indexOfImageToBeSelected: number) {
        this.pendingSelectionIndex = indexOfImageToBeSelected;
        this.tryApplyPendingSelection();
    }

    private tryApplyPendingSelection() {
        if (
            this.pendingSelectionIndex === null ||
            this.imagesPage$.value === null ||
            this.loadedPage !== this.currentPage
        ) {
            return;
        }
        const idx = this.pendingSelectionIndex;
        this.pendingSelectionIndex = null;
        this.applySelection(idx);
    }

    private applySelection(indexOfImageToBeSelected: number) {
        this.annotationStateService
            .saveCommands(true, this.selectedImage$.value?.id)
            .subscribe();
        const images: ImgWithSelection[] = this.imagesPage$.value.values;
        if (images.length > 0) {
            this.selectedImage$.next(images[indexOfImageToBeSelected]);
        }
    }

    toggleBookmark() {
        if (this.selectedImage$.value === null) {
            return;
        }

        this.selectedImage$.value.isBookmarked =
            !this.selectedImage$.value.isBookmarked;
        this.updateImagesList();

        this.cacheService
            .loadData(`workspace_image_db_${this.dbId}`)
            .pipe(
                switchMap((data) => {
                    let bookmarks: string[] = data?.bookmarkedImageIds || [];

                    if (this.selectedImage$.value.isBookmarked) {
                        bookmarks.push(this.selectedImage$.value.id);
                    } else {
                        const idx = bookmarks.indexOf(
                            this.selectedImage$.value.id
                        );
                        if (idx !== -1) bookmarks.splice(idx, 1);
                    }

                    return this.cacheService.saveData(
                        `workspace_image_db_${this.dbId}`,
                        {
                            bookmarkedImageIds: bookmarks,
                        }
                    );
                })
            )
            .subscribe();
    }

    private updateImagesList() {
        this.imagesPage$.value.values.forEach((img: ImgWithSelection) => {
            if (img.id === this.selectedImage$.value.id) {
                img.isBookmarked = this.selectedImage$.value.isBookmarked;
            }
        });
    }

    changePage(newPage: number) {
        this.pageChanged$.next();
        this.currentPage = newPage;
    }
}
